import { useIntl } from "react-intl";
import {
  FieldHelperProps,
  FieldInputProps,
  FieldMetaProps,
  useFormikContext,
} from "formik";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";

import { RadioGroupFormDetails } from "../interfaces";

const RadioGroupButtons = ({
  filter,
  field,
  meta,
  helpers,
}: {
  filter: RadioGroupFormDetails;
  field: FieldInputProps<string | null | undefined>;
  meta: FieldMetaProps<string | null | undefined>;
  helpers: FieldHelperProps<string | null | undefined>;
}) => {
  const intl = useIntl();
  const { setFieldValue } = useFormikContext();
  const showError = meta.touched && !!meta.error;
  const labelId = `${field.name}-label`;

  return (
    <FormControl
      error={showError}
      disabled={filter.readonly}
      sx={{ width: "100%" }}
    >
      <FormLabel id={labelId}>
        {intl.formatMessage({ id: filter.label })}
      </FormLabel>
      <RadioGroup
        aria-labelledby={labelId}
        name={field.name}
        value={field.value ?? ""}
        onChange={(_, value) => {
          helpers.setValue(value);
          // Same contract as the other inputs: the fields that depend on
          // this choice are cleared when it changes.
          const watchedBy = filter.watchedBy ? [filter.watchedBy].flat() : [];
          watchedBy.forEach((key) =>
            setFieldValue(key.split(".").join("__"), null)
          );
        }}
        onBlur={field.onBlur}
        sx={{
          borderWidth: `1px`,
          borderColor: "divider",
          borderStyle: "solid",
          borderRadius: "6px",
          width: "100%",
          pl: 1,
        }}
      >
        {filter.options.map((option, index) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio />}
            sx={{ alignItems: "flex-start", mt: index ? 1 : 0, mr: 0 }}
            label={
              <Box sx={{ display: "flex", flexDirection: "column", pt: 1 }}>
                <Typography variant="body2">
                  {intl.formatMessage({ id: option.label })}
                </Typography>
                {option.description && (
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {intl.formatMessage({ id: option.description })}
                  </Typography>
                )}
              </Box>
            }
          />
        ))}
      </RadioGroup>
      {showError && <FormHelperText>{meta.error}</FormHelperText>}
    </FormControl>
  );
};

export default RadioGroupButtons;
